import React, { useContext, useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { DataContext } from '../components/DataContext';
import { auth, firestore } from '../utils/firebaseConfig';
import '../styles/Dashboard.css';

const extractEndDate = (weekString) => {
    const match = weekString?.replace('Period: ', '').match(/\[(\d+), (\d+), (\d+)\] to \[(\d+), (\d+), (\d+)\]/);
    return match ? new Date(Number(match[6]), Number(match[4]) - 1, Number(match[5])) : new Date(0);
};

const getWeekLabel = (weekString) => {
    const match = weekString?.replace('Period: ', '').match(/\[(\d+), (\d+), (\d+)\] to \[(\d+), (\d+), (\d+)\]/);
    if (!match) return weekString;
    const [, month1, day1, year1, month2, day2, year2] = match;
    return `${month1.padStart(2, '0')}.${day1.padStart(2, '0')}.${year1.slice(-2)} - ${month2.padStart(2, '0')}.${day2.padStart(2, '0')}.${year2.slice(-2)}`;
};

const Dashboard = () => {
    const { employeeKpis, loadingEmployee } = useContext(DataContext);
    const [userFullName, setUserFullName] = useState('');
    const [selectedRange, setSelectedRange] = useState(30);

    useEffect(() => {
        const loadName = async () => {
            const user = auth.currentUser;
            if (!user) return;
            try {
                const snapshot = await getDoc(doc(firestore, 'users', user.uid));
                const profile = snapshot.data();
                setUserFullName(profile?.firstName || profile?.lastName
                    ? `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim()
                    : user.email);
            } catch (error) {
                console.error('Error loading dashboard profile:', error);
                setUserFullName(user.email);
            }
        };
        loadName();
    }, []);

    const userKpis = useMemo(() => employeeKpis.filter((item) => item.employee === userFullName), [employeeKpis, userFullName]);
    const periods = useMemo(() => [...new Set(userKpis.map((item) => item.week_summary).filter(Boolean))]
        .sort((a, b) => extractEndDate(b) - extractEndDate(a)), [userKpis]);

    const averages = useMemo(() => {
        if (!userFullName) return null;
        const now = Date.now();
        const relevant = userKpis.filter((item) => {
            const end = extractEndDate(item.week_summary).getTime();
            return end > 0 && now >= end && now - end <= selectedRange * 24 * 60 * 60 * 1000;
        });
        if (!relevant.length) return null;

        const total = (field) => relevant.reduce((sum, item) => sum + (Number(item[field]) || 0), 0);
        const hours = total('hours');
        const sales = total('sales');
        const totalGp = total('total_gp');
        const positions = relevant.map((kpi) => {
            const ranked = employeeKpis
                .filter((item) => item.week_summary === kpi.week_summary)
                .sort((a, b) => ((Number(b.sales) || 0) / (Number(b.hours) || 1)) - ((Number(a.sales) || 0) / (Number(a.hours) || 1)));
            const index = ranked.findIndex((item) => item.employee === userFullName);
            return index >= 0 ? index + 1 : ranked.length;
        });

        return {
            avgPosition: positions.reduce((sum, value) => sum + value, 0) / positions.length,
            salesPerHour: hours ? sales / hours : 0,
            avgSalesBonus: total('sales_bonus') / relevant.length,
            gpPerHour: hours ? totalGp / hours : 0,
            avgTotalGp: totalGp / relevant.length,
            avgEstComm: total('est_commission') / relevant.length,
            avgSalesCount: sales / relevant.length,
            avgRepairsCount: total('repairs') / relevant.length,
        };
    }, [employeeKpis, selectedRange, userFullName, userKpis]);

    const renderWeekCard = (week) => {
        const kpi = userKpis.find((item) => item.week_summary === week);
        if (!kpi) return null;
        const hours = Number(kpi.hours) || 0;
        const sales = Number(kpi.sales) || 0;
        const ratio = hours ? (sales / hours) * 100 : 0;
        const tier = ratio >= 100 ? 'Six' : ratio >= 80 ? 'Five' : ratio >= 60 ? 'Four' : ratio >= 40 ? 'Three' : ratio >= 20 ? 'Two' : 'One';
        return (
            <div className="week-summary-card" key={week}>
                <h2>{getWeekLabel(week)}</h2>
                <p><strong>Tier:</strong> {tier} ({ratio.toFixed(2)})</p>
                <p><strong>Total GP:</strong> {Number.isFinite(Number(kpi.total_gp)) ? `$${Number(kpi.total_gp).toFixed(2)}` : 'N/A'}</p>
                <p><strong>GP / Hour:</strong> {Number.isFinite(Number(kpi.gp_per_hour)) ? `$${Number(kpi.gp_per_hour).toFixed(2)}` : 'N/A'}</p>
                <p><strong>Estimated Commission:</strong> {Number.isFinite(Number(kpi.est_commission)) ? `$${Number(kpi.est_commission).toFixed(2)}` : 'N/A'}</p>
                <p><strong>Sales:</strong> {sales || 'N/A'}</p>
                <p><strong>Repairs:</strong> {kpi.repairs ?? 'N/A'}</p>
                <p><strong>Hours Worked:</strong> {hours ? `${hours.toFixed(1)} hrs` : 'N/A'}</p>
            </div>
        );
    };

    return (
        <div className="dashboard-container">
            <h1>Welcome, {userFullName}</h1>
            {loadingEmployee ? <p>Loading employee data…</p> : (
                <>
                    <div className="averages-section">
                        <div className="range-selector">
                            {[30, 60, 90].map((range) => <button type="button" key={range} className={range === selectedRange ? 'active' : ''} onClick={() => setSelectedRange(range)}>{range} Days</button>)}
                        </div>
                        <div className="averages-card">
                            <h2>Averages (Last {selectedRange} Days)</h2>
                            {!averages ? <p>No data found for the last {selectedRange} days.</p> : (
                                <>
                                    <p><strong>Position (Avg):</strong> {averages.avgPosition.toFixed(2)}</p>
                                    <p><strong>Sales / Hr:</strong> {averages.salesPerHour.toFixed(2)}</p>
                                    <p><strong>Sales Bonus (Avg):</strong> ${averages.avgSalesBonus.toFixed(2)}</p>
                                    <p><strong>GP / Hr:</strong> ${averages.gpPerHour.toFixed(2)}</p>
                                    <p><strong>Total GP (Avg):</strong> ${averages.avgTotalGp.toFixed(2)}</p>
                                    <p><strong>Est. Commission (Avg):</strong> ${averages.avgEstComm.toFixed(2)}</p>
                                    <p><strong># of Sales (Avg):</strong> {averages.avgSalesCount.toFixed(2)}</p>
                                    <p><strong># of Repairs (Avg):</strong> {averages.avgRepairsCount.toFixed(2)}</p>
                                </>
                            )}
                        </div>
                    </div>
                    <h3>Your Weekly Performance</h3>
                    {!periods.length ? <p>No weekly KPI data found.</p> : <div className="week-carousel">{periods.map(renderWeekCard)}</div>}
                </>
            )}
        </div>
    );
};

export default Dashboard;
